import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ArrowLeft, CheckCircle2, AlertCircle, Camera } from "lucide-react";

export default function QRScanner() {
  const { eventId } = useParams();
  const [lastScan, setLastScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const scannerRef = useRef(null);
  const containerId = "qr-reader";
  const lastCodeRef = useRef({ code: null, at: 0 });

  const handleScan = async (code) => {
    const now = Date.now();
    // Debounce same code within 3 seconds
    if (lastCodeRef.current.code === code && now - lastCodeRef.current.at < 3000) {
      return;
    }
    lastCodeRef.current = { code, at: now };

    try {
      const { data } = await api.post(`/events/${eventId}/scan`, {
        registration_id: code,
      });
      const ticketCount = Math.max(1, data.registration?.ticket_count || 1);
      const checkedInCount = Math.max(0, data.registration?.checked_in_count || 0);
      if (data.status === "checked_in") {
        toast.success(
          `Checked in: ${data.registration.name} (${checkedInCount}/${ticketCount})`
        );
      } else if (data.status === "already_checked_in") {
        toast.warning(
          `All tickets already checked in: ${data.registration.name} (${checkedInCount}/${ticketCount})`
        );
      }
      setLastScan({ status: data.status, reg: data.registration });
    } catch (err) {
      toast.error(formatApiError(err));
      setLastScan({ status: "error", message: formatApiError(err) });
    }
  };

  const start = async () => {
    setError("");
    const qr = new Html5Qrcode(containerId);
    scannerRef.current = qr;
    try {
      await qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        (decoded) => handleScan(decoded),
        () => {}
      );
      setScanning(true);
    } catch (e) {
      setError(
        "Camera access denied or unavailable. Please allow camera permission."
      );
    }
  };

  const stop = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => {
    return () => {
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Top bar */}
      <div className="p-4 flex items-center justify-between border-b border-zinc-800">
        <Link
          to={`/organizer/events/${eventId}`}
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          data-testid="back-to-event"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <p className="overline text-zinc-500">Scanner</p>
      </div>

      {/* Scanner surface */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div
          id={containerId}
          className="relative w-full max-w-md aspect-square bg-zinc-900 rounded-md overflow-hidden border border-zinc-800"
          data-testid="qr-scanner-view"
        >
          {!scanning && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center p-6">
              <Camera className="w-10 h-10 text-zinc-500" strokeWidth={1.5} />
              <p className="text-zinc-400 text-sm max-w-xs leading-relaxed">
                Allow camera access to scan participant QR codes at entry.
              </p>
              <Button
                onClick={start}
                className="bg-white text-black hover:bg-zinc-200 rounded-md"
                data-testid="start-scanner-button"
              >
                Start camera
              </Button>
              {error && (
                <p className="text-red-400 text-xs" data-testid="scanner-error">
                  {error}
                </p>
              )}
            </div>
          )}

          {scanning && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="w-[260px] h-[260px] border-2 border-white rounded-md"></div>
            </div>
          )}
        </div>

        {scanning && (
          <Button
            onClick={stop}
            variant="outline"
            className="mt-6 border-zinc-700 bg-transparent text-white hover:bg-zinc-900 rounded-md"
            data-testid="stop-scanner-button"
          >
            Stop camera
          </Button>
        )}

        {/* Last scan result */}
        {lastScan && (
          <div
            className="mt-8 w-full max-w-md p-5 border border-zinc-800 rounded-md bg-zinc-900"
            data-testid="last-scan-result"
          >
            {lastScan.status === "checked_in" && (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="overline text-green-500 mb-1">Checked in</p>
                  <p className="font-heading text-xl font-semibold tracking-tight">
                    {lastScan.reg.name}
                  </p>
                  <p className="text-sm text-zinc-400">
                    Sponsor: {lastScan.reg.sponsor_name}
                  </p>
                  <p className="text-sm text-zinc-400">
                    Checked in: {Math.max(0, lastScan.reg.checked_in_count || 0)} / {Math.max(1, lastScan.reg.ticket_count || 1)}
                  </p>
                </div>
              </div>
            )}
            {lastScan.status === "already_checked_in" && (
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="overline text-orange-500 mb-1">Already checked in</p>
                  <p className="font-heading text-xl font-semibold tracking-tight">
                    {lastScan.reg.name}
                  </p>
                  <p className="text-sm text-zinc-400">
                    Checked in: {Math.max(0, lastScan.reg.checked_in_count || 0)} / {Math.max(1, lastScan.reg.ticket_count || 1)}
                  </p>
                </div>
              </div>
            )}
            {lastScan.status === "error" && (
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="overline text-red-500 mb-1">Invalid</p>
                  <p className="text-sm text-zinc-300">{lastScan.message}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
